"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi } from "@/lib/api";

interface User {
  id: string;
  email: string;
  phone?: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  firmId?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  firmId: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; phone?: string; password: string; firstName: string; lastName: string; role?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedRefreshToken = localStorage.getItem("refreshToken");
    const storedUser = localStorage.getItem("user");
    if (storedToken && storedUser) {
      const userData = JSON.parse(storedUser);
      setToken(storedToken);
      setUser(userData);
      setRefreshToken(storedRefreshToken);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    const { user: userData, token: authToken, refreshToken: rt } = response.data;
    setUser(userData);
    setToken(authToken);
    setRefreshToken(rt);
    localStorage.setItem("token", authToken);
    localStorage.setItem("refreshToken", rt);
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  const register = useCallback(async (data: { email: string; phone?: string; password: string; firstName: string; lastName: string; role?: string }) => {
    const response = await authApi.register(data);
    const { user: userData, token: authToken, refreshToken: rt } = response.data;
    setUser(userData);
    setToken(authToken);
    setRefreshToken(rt);
    localStorage.setItem("token", authToken);
    localStorage.setItem("refreshToken", rt);
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  const logout = useCallback(async () => {
    try {
      if (refreshToken) {
        await authApi.logout({ refreshToken });
      }
    } catch {
    } finally {
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
  }, [refreshToken]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const rt = localStorage.getItem("refreshToken");
      if (!rt) throw new Error("No refresh token");
      const response = await authApi.refreshToken({ refreshToken: rt });
      const { token: newToken, refreshToken: newRt } = response.data;
      setToken(newToken);
      setRefreshToken(newRt);
      localStorage.setItem("token", newToken);
      localStorage.setItem("refreshToken", newRt);
    } catch {
      setUser(null);
      setToken(null);
      setRefreshToken(null);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
  }, []);

  const firmId = user?.firmId || null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        refreshToken,
        firmId,
        loading,
        login,
        register,
        logout,
        refreshAccessToken,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
